import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Menu, X, ShieldAlert } from "lucide-react";
import { useState } from "react";
import BrandLogo from "./BrandLogo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/search", label: "Find a business" },
    { to: "/offers", label: "Today's offers" },
    ...(user && user.role !== "admin" ? [{ to: "/boost", label: "Boost" }] : []),
    { to: "/faq", label: "FAQ" },
  ];

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-zinc-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <BrandLogo size="md" />
          <div className="flex flex-col leading-none">
            <span className="font-display text-xl font-bold tracking-tight">TradeFlow</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">UK · Small Business Directory</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1).replace('/', '-')}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(l.to) ? "bg-black text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link
              to="/admin/listings"
              data-testid="nav-admin"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                loc.pathname.startsWith("/admin") ? "bg-black text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              <ShieldAlert className="w-3.5 h-3.5" /> Admin
            </Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {user.role !== "admin" && (
                <Link to="/dashboard" data-testid="nav-dashboard">
                  <Button variant="outline" className="rounded-md">My listing</Button>
                </Link>
              )}
              <Button
                onClick={async () => { await logout(); nav("/"); }}
                variant="ghost"
                data-testid="logout-btn"
                className="rounded-md"
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="rounded-md">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-[#FFCC00] hover:bg-[#E6B800] text-black rounded-md">List your business</Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-zinc-100"
              >
                {l.label}
              </Link>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin/listings" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-medium rounded-md hover:bg-zinc-100">Admin</Link>
            )}
            <div className="h-px bg-zinc-200 my-2" />
            {user ? (
              <>
                {user.role !== "admin" && <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2 font-medium">My listing</Link>}
                <button onClick={async () => { await logout(); nav("/"); setOpen(false); }} className="text-left px-3 py-2 font-medium">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 font-medium">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="px-3 py-2 font-medium bg-[#FFCC00] rounded-md">List your business</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
